import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink, NavigationEnd } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { MenuService } from '../../services/menu.service';
import { MenuItem } from '../../models/menu-item.model';
import packages from '../../../../../package.json';
import { StaticSegmentsService } from '../../../utils/static-segment.service';
import { filter, Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [CommonModule, MatListModule, MatIconModule, RouterLink, FaIconComponent],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss'
})
export class SidenavComponent implements OnInit, OnDestroy {

  protected readonly faXmark = faXmark;
  protected readonly environment = environment;
  private menuService = inject(MenuService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private staticSegmentsService = inject(StaticSegmentsService);
  private routerSubscription?: Subscription;

  // LocalStorage Key
  private readonly SUBMENU_STATE_KEY = 'sidenav-submenu-state';

  version = packages.version;
  buildDate = packages.builddate;

  menu: MenuItem[];
  activeItem: MenuItem | null = null;
  activeSubItem: MenuItem | null = null;
  activeSubSubItem: MenuItem | null = null;
  showLabels: boolean = false;
  showSubmenu: boolean = false;
  @Input() domain!: string;
  impressum: any = {
    'sug': 'sommer-gossmann.com',
    'ems': 'em-sourcing.com'
  };

  ngOnInit() {
    this.menu = this.menuService.getMenu();
    this.autoExpandItems(this.menu);

    // Submenu-Zustand aus LocalStorage laden
    this.loadSubmenuState();

    // Initiale Route setzen
    const currentRoute = this.staticSegmentsService.getCleanPathSnapshot(this.route.root);
    if (Array.isArray(this.menu)) {
      this.setActiveItemsFromRoute(currentRoute);
    }

    // Auf Routenänderungen hören
    this.routerSubscription = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        const newRoute = this.staticSegmentsService.getCleanPathSnapshot(this.route.root);
        this.setActiveItemsFromRoute(newRoute);
      });
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
  }

  /**
   * Lädt den Submenu-Zustand aus LocalStorage
   */
  private loadSubmenuState(): void {
    const savedState = localStorage.getItem(this.SUBMENU_STATE_KEY);
    if (savedState !== null) {
      this.showSubmenu = savedState === 'true';
    }
  }

  /**
   * Speichert den Submenu-Zustand in LocalStorage
   */
  private saveSubmenuState(): void {
    localStorage.setItem(this.SUBMENU_STATE_KEY, String(this.showSubmenu));
  }

  private setActiveItemsFromRoute(currentRoute: string) {
    // Wenn Route /home ist, alles zurücksetzen
    if (currentRoute === '/home' || currentRoute === '/') {
      this.activeItem = null;
      this.activeSubItem = null;
      this.activeSubSubItem = null;
      this.showSubmenu = false;
      this.saveSubmenuState();
      return;
    }

    const routeParts = currentRoute.split('/');
    const mainRole = routeParts[1];

    const foundMainItem = this.menu.find((item: MenuItem) => item.role === mainRole);
    if (foundMainItem) {
      this.activeItem = foundMainItem;

      if (localStorage.getItem(this.SUBMENU_STATE_KEY) === null) {
        this.showSubmenu = true;
        this.saveSubmenuState();
      }

      if (foundMainItem.children && foundMainItem.children.length > 0) {
        for (const subItem of foundMainItem.children) {
          // Erst in den Grandchildren suchen (dritte Ebene)
          if (subItem.children && subItem.children.length > 0) {
            const foundGrandChild = subItem.children.find((gc: MenuItem) =>
              gc.route === currentRoute || currentRoute.startsWith(gc.route + '/')
            );
            if (foundGrandChild) {
              this.activeSubItem = subItem;
              this.activeSubSubItem = foundGrandChild;
              subItem.expanded = true;
              break;
            }
          }
          // Dann den SubItem selbst prüfen
          if (subItem.route && (subItem.route === currentRoute || currentRoute.startsWith(subItem.route + '/'))) {
            this.activeSubItem = subItem;
            this.activeSubSubItem = null;
            break;
          }
        }
      }
    }
  }

  toggleLabels() {
    this.showLabels = !this.showLabels;
  }

  selectRoot(item: MenuItem) {
    this.activeItem = item;
    this.showSubmenu = true;
    this.saveSubmenuState(); // Zustand speichern
  }

  selectSubItem(item: MenuItem) {
    if (!item.disabled) {
      this.activeSubItem = item;
      this.activeSubSubItem = null;
    }
  }

  selectSubSubItem(item: MenuItem) {
    if (!item.disabled) {
      this.activeSubSubItem = item;
    }
  }

  private autoExpandItems(items: MenuItem[]): void {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        item.expanded = true;
        this.autoExpandItems(item.children);
      }
    }
  }

  closeSubMenu() {
    this.showSubmenu = false;
    this.saveSubmenuState(); // Zustand speichern
  }
}
